import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import DramaCard from '@/components/base/DramaCard';
import { actors } from '@/mocks/actors';
import { dramas } from '@/mocks/dramas';

export default function ActorDetail() {
  const { id } = useParams<{ id: string }>();

  const actor = useMemo(() => actors.find((a) => a.id === id), [id]);
  const works = useMemo(
    () => (actor ? dramas.filter((d) => actor.dramaIds.includes(d.id)) : []),
    [actor],
  );

  if (!actor) {
    return (
      <SiteLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
          <i className="ri-user-unfollow-line text-5xl text-foreground-600" />
          <h1 className="mt-4 text-xl font-semibold text-foreground-100">暂无演员资料</h1>
          <p className="mt-2 text-sm text-foreground-400">可能该演员信息已移除或链接有误</p>
          <Link to="/actors" className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
            返回演员列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/actors" className="hover:text-primary-400 transition-colors cursor-pointer">演员资料</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-300">{actor.name}</span>
        </nav>

        <div className="flex flex-col md:flex-row gap-8 md:gap-10">
          <div className="w-full md:w-72 shrink-0">
            <div className="overflow-hidden rounded-lg border border-background-800 bg-background-800 aspect-[3/4]">
              <img
                src={actor.avatar}
                alt={`${actor.name} 演员照`}
                title={`${actor.name} - 成人短剧演员资料`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-50 font-heading">{actor.name}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {actor.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-accent-900/50 px-3 py-1 text-xs text-accent-200">
                  {tag}
                </span>
              ))}
            </div>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
              <div className="rounded-lg border border-background-800 bg-background-900/50 p-4">
                <dt className="text-xs text-foreground-500">地区</dt>
                <dd className="mt-1 text-sm text-foreground-100">{actor.region}</dd>
              </div>
              <div className="rounded-lg border border-background-800 bg-background-900/50 p-4">
                <dt className="text-xs text-foreground-500">出生日期</dt>
                <dd className="mt-1 text-sm text-foreground-100">{actor.birth}</dd>
              </div>
            </dl>

            <section className="mt-8">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-50 mb-4">
                <span className="block w-1 h-5 rounded-full bg-primary-500" />
                演员简介
              </h2>
              <p className="text-sm md:text-base leading-loose text-foreground-300">{actor.bio}</p>
            </section>
          </div>
        </div>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 text-lg md:text-xl font-bold text-foreground-50 mb-5">
            <span className="block w-1 h-5 rounded-full bg-accent-500" />
            参与短剧
            <span className="text-xs font-normal text-foreground-500">（{works.length} 部）</span>
          </h2>
          {works.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {works.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-700 bg-background-900/40 py-16">
              <i className="ri-movie-line text-4xl text-foreground-600" />
              <p className="mt-3 text-sm text-foreground-400">暂无关联作品</p>
            </div>
          )}
        </section>
      </div>
    </SiteLayout>
  );
}